import CloseIcon from "@mui/icons-material/Close";
import {IconButton} from "@mui/material";
import {closeSnackbar, SnackbarOrigin, SnackbarProvider} from "notistack";
import React, {createContext, useCallback, useContext, useEffect, useState} from "react";

const DEFAULT_ANCHOR: SnackbarOrigin = {vertical: "top", horizontal: "right"};
// Fullscreen surfaces keep their primary actions (Save, table toolbar, exit fullscreen) in the
// top-right corner, exactly where the default toast would land.
const FULLSCREEN_ANCHOR: SnackbarOrigin = {vertical: "bottom", horizontal: "center"};

type RegisterFullscreen = () => () => void;

// No-op outside the provider (tests, storybook-style renders) so callers never have to guard.
const FullscreenSnackbarContext = createContext<RegisterFullscreen>(() => () => {});

/**
 * Single notistack provider for the whole dashboard.
 *
 * There must be exactly one: notistack binds its module-level `enqueueSnackbar` in the provider
 * constructor and never rebinds on unmount, so a nested per-page provider would hijack the global
 * binding for the rest of the session. Instead, fullscreen surfaces register themselves through
 * `useFullscreenSnackbarAnchor` and this provider swaps the anchor while any of them is active.
 * notistack resolves `anchorOrigin` at enqueue time, so toasts already on screen keep their spot.
 */
export function DashboardSnackbarProvider({children}: {children: React.ReactNode}) {
    // Ref-counted so nested surfaces (a table entering fullscreen inside a fullscreen page)
    // release the anchor only once the last one is gone.
    const [fullscreenCount, setFullscreenCount] = useState(0);

    const registerFullscreen = useCallback<RegisterFullscreen>(() => {
        setFullscreenCount(count => count + 1);
        return () => setFullscreenCount(count => count - 1);
    }, []);

    return (
        <FullscreenSnackbarContext.Provider value={registerFullscreen}>
            <SnackbarProvider
                maxSnack={3}
                autoHideDuration={5000}
                anchorOrigin={fullscreenCount > 0 ? FULLSCREEN_ANCHOR : DEFAULT_ANCHOR}
                action={snackbarId => (
                    <IconButton onClick={() => closeSnackbar(snackbarId)} color="inherit">
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                )}
            >
                {children}
            </SnackbarProvider>
        </FullscreenSnackbarContext.Provider>
    );
}

/**
 * Declares the calling component a fullscreen surface: while it is mounted (and `active`),
 * new toasts anchor bottom-center instead of top-right. Call it from every page rendered outside
 * the regular layouts and from any component that takes over the viewport on its own.
 */
export function useFullscreenSnackbarAnchor(active = true) {
    const registerFullscreen = useContext(FullscreenSnackbarContext);

    useEffect(() => {
        if (!active) return;
        return registerFullscreen();
    }, [active, registerFullscreen]);
}
